// hooks/useUiVersionToggle.js
import { useMemo, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { navigateWithForceParam, isV2Enabled } from '../uiVersion';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { NotificationContext } from '../contexts/NotificationContext';
import { splitUserId } from '../helpers';
import config from '../config';

export function useUiVersionToggle() {
	const { userState } = useContext(CurrentUserContext) || {};
	const { notify } = useContext(NotificationContext) || {};
	const [optimisticOn, setOptimisticOn] = useState(null);
	const [isPending, setIsPending] = useState(false);

	const userVersion = userState?.currUser?.uiVersion;
	const cookieV2 = isV2Enabled();

	const onV2 = useMemo(() => {
		if (optimisticOn !== null) return optimisticOn;
		if (userVersion === 'v1') return false;
		if (userVersion === 'v2') return true;
		return cookieV2;
	}, [optimisticOn, userVersion, cookieV2]);

	const setUiVersion = useCallback(
		async (nextValue) => {
			const nextVersion = nextValue ? 'v2' : 'v1';
			setOptimisticOn(nextValue);
			setIsPending(true);

			try {
				const userId = splitUserId(userState?.currUser?.sub);
				const url = `${config.auth}/v1/user/user.update/${userId}`;

				await axios.patch(url, { uiVersion: nextVersion });

				navigateWithForceParam(nextVersion);
				// hard nav, nothing after this really matters
			} catch (err) {
				console.error('Failed to update uiVersion', err);
				if (notify) {
					notify({
						type: 'error',
						message: 'Could not switch UI version. Try again.',
					});
				}
				setOptimisticOn(null);
			} finally {
				setIsPending(false);
			}
		},
		[userState?.currUser?.sub, notify]
	);

	const toggleUiVersion = useCallback(
		async (maybeNextValue) => {
			let nextValue;
			if (typeof maybeNextValue === 'boolean') nextValue = maybeNextValue;
			else nextValue = !onV2;

			return setUiVersion(nextValue);
		},
		[onV2, setUiVersion]
	);

	return {
		onV2,
		isPending,
		setUiVersion,
		toggleUiVersion,
	};
}
